import { bidsAreHidden, priceIsHidden } from '@auctisync/api-client';
import { formatCents, isHotLot, thumbUrl, type BidderStanding } from '@auctisync/shared';
import { Link } from 'react-router-dom';
import { Countdown } from '../Countdown';

/** The fields a card needs. Deliberately narrow so any lot query shape fits. */
export interface LotCardLot {
  id: string;
  lot_number: number;
  title: string | null;
  current_bid_cents: number | null;
  starting_bid_cents: number;
  /**
   * Null when the house hides the count from this viewer (a live lot from the
   * engine): the card prints no count and no Hot badge.
   */
  bid_count: number | null;
  ends_at: string | null;
  has_reserve: boolean | null;
}

/**
 * ONE lot card for every grid on the site — home, browse, search, the live
 * room's grid view, a sale's catalogue.
 *
 * It is a component rather than a snippet each page repeats because the card is
 * where a bidder reads a price and a clock, and five copies of that would drift
 * into five different ideas of what a lot costs. `.w-card` from the handoff.
 *
 * THE WHOLE CARD IS A LINK, not a div with a click handler. A bidder who wants
 * a lot in a new tab is doing the most ordinary thing on an auction site, and a
 * click handler cannot be middle-clicked, opened in a background tab, or
 * previewed on hover.
 *
 * OFFLINE FREEZES THE CLOCK. A countdown that keeps ticking while we cannot
 * reach the server is the page telling a lie about a number that decides
 * whether someone still has time to bid.
 *
 * A FIGURE THE HOUSE HIDES IS NOT PRINTED (2026-10-08). Fed live figures from
 * the engine, a price can be hidden from this viewer and a count can be
 * withheld; the shared rule (`priceIsHidden`, `bidsAreHidden` — the same one
 * behind the Lots page's `LiveLot`) decides, and the card shows a dash.
 */
export function LotCard({
  lot,
  auctionSlug,
  photoUrl,
  nowServerMs,
  standing,
  offline = false,
  watched,
  onToggleWatch,
}: {
  lot: LotCardLot;
  auctionSlug: string;
  photoUrl?: string | null;
  nowServerMs: () => number;
  standing?: BidderStanding | undefined;
  offline?: boolean;
  /** Omit both to render a card with no watch control (e.g. for a guest). */
  watched?: boolean;
  onToggleWatch?: (lotId: string) => void;
}) {
  const noReserve = lot.has_reserve === false;
  const priceHidden = priceIsHidden(lot.current_bid_cents, lot.bid_count);
  const bidsHidden = bidsAreHidden(lot.bid_count);
  return (
    <Link
      to={`/auctions/${auctionSlug}/lots/${String(lot.lot_number)}`}
      className="w-card"
      style={{ position: 'relative' }}
    >
      <div className="ph">
        {noReserve ? (
          <span className="w-badge">
            <span className="tag t-nr">No reserve</span>
          </span>
        ) : null}
        {onToggleWatch ? (
          <button
            type="button"
            className="w-fav"
            aria-pressed={watched ?? false}
            aria-label={watched ? 'Remove from watchlist' : 'Watch this lot'}
            style={watched ? { color: 'var(--semantic-closing)' } : undefined}
            onClick={(e) => {
              // The card is a link; watching must not navigate.
              e.preventDefault();
              e.stopPropagation();
              onToggleWatch(lot.id);
            }}
          >
            {watched ? '♥' : '♡'}
          </button>
        ) : null}
        {photoUrl ? <img src={thumbUrl(photoUrl)} alt="" loading="lazy" /> : <em>no photograph</em>}
      </div>
      <div className="w-card-b">
        <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
          <span className="w-lbl">Lot {lot.lot_number}</span>
          {standing === 'winning' ? <span className="tag t-win">Winning</span> : null}
          {standing === 'outbid' ? <span className="tag t-out">Outbid</span> : null}
        </div>
        <div className="w-card-t">{lot.title ?? '(untitled)'}</div>
        <div className="w-card-f">
          <div>
            <div className="w-price" style={{ fontSize: 18 }}>
              {priceHidden ? '—' : formatCents(lot.current_bid_cents ?? lot.starting_bid_cents)}
            </div>
            <div
              className="muted"
              style={{ fontSize: 11.5, marginTop: 1, display: 'flex', gap: 6 }}
            >
              {bidsHidden ? null : (
                <span>
                  {lot.bid_count} bid{lot.bid_count === 1 ? '' : 's'}
                </span>
              )}
              {/* 🔥 Hot — more than 20 bids, from the one shared rule the phone's
                  LotTile and the results page also use. A hidden count takes
                  the badge with it: "Hot" is a count stated in words. */}
              {isHotLot(lot.bid_count ?? 0, bidsHidden) ? (
                <span title="More than 20 bids">🔥 Hot</span>
              ) : null}
            </div>
          </div>
          {offline ? (
            <span className="cd muted">held</span>
          ) : (
            <Countdown endsAt={lot.ends_at} nowServerMs={nowServerMs} className="cd" />
          )}
        </div>
      </div>
    </Link>
  );
}
